import React from 'react'
import styled from 'styled-components'
import { DateDisplay } from 'lib/DateDisplay'
import { ErrorBoundary } from 'lib/ErrorBoundary'
import { Link } from 'lib/Link'
import { ShowForMobile } from 'lib/ShowComponent'
import { SocialMediaLink } from 'lib/SocialMediaLink'
import { TextWithLimit } from 'lib/TextWithLimit'
import { SpottedBy } from './SpottedBy'
import { PropertiesList } from './components/PropertiesList'
import { useCurrentBrand } from './context/CurrentBrandProvider'

const Container = styled.div`
  margin-top: 24px;
`

const CategoryLinkContainer = styled.span`
  &::after {
    content: ', ';
  }

  &:last-child::after {
    content: '';
  }

  & + & {
    margin-left: 3px;
  }
`

const SocialLinksList = styled(PropertiesList)`
  &:empty {
    display: none;
  }
`

const Founder = styled.div`
  display: flex;
  justify-content: flex-end;

  &::after {
    content: ', ';
  }

  &:last-child::after {
    content: '';
  }

  & + & {
    margin-left: 3px;
  }
`

const DescriptionFromBrand = styled(PropertiesList)`
  flex-direction: column;
  white-space: pre-wrap;
`

export const Facts = () => {
  const {
    brand: { id, title, company, people, categories, shipsTo, descriptionFromBrand }
  } = useCurrentBrand()
  const founders = people?.founders || []
  const hasAnySocialLink =
    company?.instagram ||
    company?.twitter ||
    company?.linkedinUrl ||
    company?.facebookUrl ||
    company?.tiktokUrl ||
    company?.youtubeUrl ||
    company?.pintrestUrl

  return (
    <Container>
      {categories.length > 0 && (
        <PropertiesList title={categories.length > 1 ? 'Categories' : 'Category'}>
          {[...categories]
            .sort((cat) => (cat.parentId ? 1 : -1)) // Top level categories -> Subcategories (less specific to more specific)
            .map((cat) => (
              <CategoryLinkContainer key={cat.id}>
                <Link underlineOnNormalState href={`/categories/${cat.slug}`} prefetch={false}>
                  {cat.name}
                </Link>
              </CategoryLinkContainer>
            ))}
        </PropertiesList>
      )}
      <PropertiesList title="Ships to" properties={shipsTo} />
      {company?.dateFounded && (
        <ErrorBoundary name="Brand facts founded">
          <PropertiesList title="Founded">
            {company.dateFounded.match(/-01-01$/) ? company.dateFounded.replace(/-01-01$/, '') : <DateDisplay date={company.dateFounded} />}
          </PropertiesList>
        </ErrorBoundary>
      )}
      {company?.dateLaunched && (
        <ErrorBoundary name="Brand facts launched">
          <PropertiesList title="Launched">
            <DateDisplay date={company.dateLaunched} />
          </PropertiesList>
        </ErrorBoundary>
      )}
      {company?.hqLocation && <PropertiesList title="Headquarters">{company.hqLocation}</PropertiesList>}
      {founders.length > 0 && (
        <PropertiesList title={founders.length === 1 ? 'Founder' : 'Founders'}>
          {founders.map((founder) => (
            <Founder key={founder.name}>{founder.name}</Founder>
          ))}
        </PropertiesList>
      )}

      {people?.demographics && (
        <PropertiesList
          title="Founder attributes"
          properties={people.demographics.map((demographic) => {
            const prettified = demographic.replace('lgbtq', 'LGBTQ+').replace('black-owned', 'black-founded')
            const capitalized = prettified.charAt(0).toUpperCase() + prettified.slice(1)
            return capitalized
          })}
        />
      )}
      {company?.certificates && <PropertiesList title="Certifications" properties={company.certificates} />}
      {hasAnySocialLink && (
        <SocialLinksList title="Social links">
          {company?.instagram && <SocialMediaLink brandId={id} brandTitle={title} type="instagram" value={company.instagram} />}
          {company?.twitter && <SocialMediaLink brandId={id} brandTitle={title} type="twitter" value={company.twitter} />}
          {company?.linkedinUrl && <SocialMediaLink brandId={id} brandTitle={title} type="linkedinUrl" value={company.linkedinUrl} />}
          {company?.facebookUrl && <SocialMediaLink brandId={id} brandTitle={title} type="facebookUrl" value={company.facebookUrl} />}
          {company?.tiktokUrl && <SocialMediaLink brandId={id} brandTitle={title} type="tiktokUrl" value={company.tiktokUrl} />}
          {company?.youtubeUrl && <SocialMediaLink brandId={id} brandTitle={title} type="youtubeUrl" value={company.youtubeUrl} />}
          {company?.pintrestUrl && <SocialMediaLink brandId={id} brandTitle={title} type="pintrestUrl" value={company.pintrestUrl} />}
        </SocialLinksList>
      )}
      {descriptionFromBrand && (
        <DescriptionFromBrand title={`About ${title}`} childrenAsProperty={false}>
          <TextWithLimit text={descriptionFromBrand} textProps={{ weight: 'regular', size: 'ps' }} />
        </DescriptionFromBrand>
      )}
      <ShowForMobile>
        <SpottedBy />
      </ShowForMobile>
    </Container>
  )
}
